import { useEffect, useMemo } from "react";
import { groupNotifications } from "@/lib/notifications";
import { useNotifications } from "@/lib/useNotifications";
import { Case } from "@/lib/useRealtimeCases";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { SlidePanel } from "@/components/ui/SlidePanel";
import { MultiSelectDropdown } from "@/components/ui/MultiSelectDropdown";

interface SignaturesSlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCaseClick: (caseId: string) => void;
  cases: Case[];
}

export function SignaturesSlidePanel({
  isOpen,
  onClose,
  onCaseClick,
  cases,
}: SignaturesSlidePanelProps) {
  // ── Centres disponibles ──────────────────────────────────────────────
  const availableCenters = useMemo(() => {
    const unique = Array.from(
      new Set(cases.map((c) => c.center).filter(Boolean))
    ) as string[];
    return unique.sort();
  }, [cases]);

  const { selectedCenters, setSelectedCenters } = useSelectedCenters();

  // Si aucun centre sélectionné et des centres disponibles, tout sélectionner
  useEffect(() => {
    if (availableCenters.length > 0 && selectedCenters.length === 0) {
      setSelectedCenters(availableCenters);
    }
  }, [availableCenters, selectedCenters.length, setSelectedCenters]);

  // ── Notifications ────────────────────────────────────────────────────
  const {
    notifications,
    loading,
    error,
    markCaseNotificationsRead,
  } = useNotifications();

  const { signatureItems } = useMemo(
    () => groupNotifications(notifications, selectedCenters),
    [notifications, selectedCenters]
  );

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleCaseClick = (caseId: string) => {
    onCaseClick(caseId);
    onClose();
  };

  const handleMarkRead = async (e: React.MouseEvent, caseId: string) => {
    e.stopPropagation();
    await markCaseNotificationsRead(caseId, "signature");
  };

  // ── Rendu ────────────────────────────────────────────────────────────
  return (
    <SlidePanel isOpen={isOpen} onClose={onClose}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="px-6 py-5 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 via-green-50/60 to-white flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shadow-sm">
              <svg
                className="w-6 h-6 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Nouvelles signatures
              </h2>
              <p className="text-sm text-gray-500">
                {signatureItems.length > 0
                  ? `${signatureItems.length} devis signé${signatureItems.length > 1 ? "s" : ""} à traiter`
                  : "Aucune signature en attente"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <MultiSelectDropdown
          label="Centres"
          value={selectedCenters}
          onChange={setSelectedCenters}
          options={availableCenters}
          placeholder="Filtrer par centre..."
          allowCustom={false}
        />
      </div>

      {/* ── Contenu ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <EmptyState icon="⏳" title="Chargement..." />
        ) : error ? (
          <EmptyState icon="⚠️" title="Impossible de charger" subtitle={error} />
        ) : signatureItems.length === 0 ? (
          <EmptyState
            icon="✍️"
            title="Aucune signature en attente"
            subtitle="Les nouvelles signatures apparaîtront ici"
          />
        ) : (
          <div className="p-4 space-y-3">
            {signatureItems.map((sig) => (
              <div
                key={sig.caseId}
                onClick={() => handleCaseClick(sig.caseId)}
                className="group relative bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-200 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  {/* Indicateur */}
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-emerald-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 min-w-0">
                    {/* Ligne 1 : Entreprise + date */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="text-sm font-bold text-gray-900 truncate">
                        {sig.companyName || sig.clientName}
                      </h4>
                      <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
                        {formatRelativeTime(sig.signedAt)}
                      </span>
                    </div>

                    {/* Ligne 2 : Client + Titre */}
                    <p className="text-sm text-gray-600 truncate mb-1.5">
                      {sig.companyName
                        ? `${sig.clientName} · ${sig.caseTitle}`
                        : sig.caseTitle}
                    </p>

                    {/* Ligne 3 : Badges */}
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                        ✍️ Devis signé{sig.quoteVersion ? ` v${sig.quoteVersion}` : ""}
                      </span>
                      {sig.center && (
                        <span className="inline-flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {sig.center}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bouton marquer lu */}
                  <button
                    onClick={(e) => handleMarkRead(e, sig.caseId)}
                    className="flex-shrink-0 p-2 rounded-lg text-gray-300 hover:text-green-600 hover:bg-green-50 opacity-0 group-hover:opacity-100 transition-all"
                    title="Marquer comme lu"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="text-5xl mb-4">{icon}</div>
      <p className="text-base font-semibold text-gray-900 mb-1">{title}</p>
      {subtitle && <p className="text-sm text-gray-500 max-w-xs">{subtitle}</p>}
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}j`;

    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return dateStr;
  }
}
